import * as Dialog from '@radix-ui/react-dialog';
import { Loader2 } from 'lucide-react';
import { Checkbox } from '../../components/ui/Checkbox';
import { DiscountDraft } from '../../queries/discounts';

interface DiscountFormDialogProps {
  draft: DiscountDraft;
  onChange: (draft: DiscountDraft) => void;
  onCancel: () => void;
  onSave: () => void;
  saving: boolean;
  error: string | null;
}

const inputClass = 'mt-1 w-full rounded-md border border-input bg-background px-2 py-2 text-sm';
// Exported so EMPTY_DRAFT can seed the same value. The input used to show
// this as a display-only fallback, which meant an untouched date field left
// start_date undefined and the save was rejected as missing.
export const todayDateString = () => new Date().toISOString().split('T')[0];

export function DiscountFormDialog({ draft, onChange, onCancel, onSave, saving, error }: DiscountFormDialogProps) {
  const isEdit = !!draft.id;

  return (
    <Dialog.Root open onOpenChange={(open) => !open && onCancel()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/40" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-full max-w-lg -translate-x-1/2 -translate-y-1/2 rounded-lg border border-border bg-background p-6 shadow-lg">
          <Dialog.Title className="text-lg font-semibold">{isEdit ? 'Edit discount' : 'Add discount'}</Dialog.Title>

          <div className="mt-4 grid grid-cols-2 gap-3">
            <label className="text-sm">
              Code
              <input
                className={`${inputClass} font-mono uppercase`}
                value={draft.code ?? ''}
                onChange={(e) => onChange({ ...draft, code: e.target.value.toUpperCase() })}
                placeholder="WELCOME10"
              />
            </label>
            <label className="text-sm">
              Name
              <input className={inputClass} value={draft.name ?? ''} onChange={(e) => onChange({ ...draft, name: e.target.value })} />
            </label>
            <label className="col-span-2 text-sm">
              Description
              <input
                className={inputClass}
                value={draft.description ?? ''}
                onChange={(e) => onChange({ ...draft, description: e.target.value })}
              />
            </label>
            <label className="text-sm">
              Type
              <select
                className={inputClass}
                value={draft.discount_type ?? 'PERCENTAGE'}
                onChange={(e) => onChange({ ...draft, discount_type: e.target.value as DiscountDraft['discount_type'] })}
              >
                <option value="PERCENTAGE">Percentage</option>
                <option value="FIXED_AMOUNT">Fixed amount</option>
                <option value="FREE_TRIAL">Free trial</option>
              </select>
            </label>
            <label className="text-sm">
              Value {draft.discount_type === 'PERCENTAGE' ? '(%)' : draft.discount_type === 'FIXED_AMOUNT' ? '($)' : '(days)'}
              <input
                type="number"
                step="0.01"
                className={inputClass}
                value={draft.discount_value ?? ''}
                onChange={(e) => onChange({ ...draft, discount_value: e.target.value ? parseFloat(e.target.value) : undefined })}
              />
            </label>
            <label className="text-sm">
              Applies to
              <select
                className={inputClass}
                value={draft.applies_to ?? 'ALL'}
                onChange={(e) => onChange({ ...draft, applies_to: e.target.value as DiscountDraft['applies_to'] })}
              >
                <option value="ALL">All</option>
                <option value="BASE_PACKAGES">Base packages</option>
                <option value="ADDONS">Addons</option>
                <option value="SPECIFIC_PACKAGES">Specific packages</option>
              </select>
            </label>
            <label className="text-sm">
              Stripe duration
              <select
                className={inputClass}
                value={draft.stripe_duration ?? 'ONCE'}
                onChange={(e) => {
                  const stripe_duration = e.target.value as DiscountDraft['stripe_duration'];
                  onChange({
                    ...draft,
                    stripe_duration,
                    stripe_duration_in_months: stripe_duration === 'REPEATING' ? draft.stripe_duration_in_months : null,
                  });
                }}
              >
                <option value="ONCE">Once</option>
                <option value="REPEATING">Repeating</option>
                <option value="FOREVER">Forever</option>
              </select>
            </label>
            {draft.stripe_duration === 'REPEATING' && (
              <label className="text-sm">
                Applies for (months)
                <input
                  type="number"
                  min={1}
                  step={1}
                  className={inputClass}
                  value={draft.stripe_duration_in_months ?? ''}
                  onChange={(e) =>
                    onChange({ ...draft, stripe_duration_in_months: e.target.value ? parseInt(e.target.value, 10) : null })
                  }
                  placeholder="e.g. 3"
                />
              </label>
            )}
            <label className="text-sm">
              Start date
              <input
                type="date"
                className={inputClass}
                value={draft.start_date ?? ''}
                onChange={(e) => onChange({ ...draft, start_date: e.target.value })}
              />
            </label>
            <label className="text-sm">
              End date (optional)
              <input
                type="date"
                className={inputClass}
                value={draft.end_date ?? ''}
                onChange={(e) => onChange({ ...draft, end_date: e.target.value || null })}
              />
            </label>
            <label className="text-sm">
              Usage limit (total, optional)
              <input
                type="number"
                className={inputClass}
                value={draft.usage_limit ?? ''}
                onChange={(e) => onChange({ ...draft, usage_limit: e.target.value ? parseInt(e.target.value, 10) : null })}
              />
            </label>
            <label className="text-sm">
              Usage limit per user
              <input
                type="number"
                className={inputClass}
                value={draft.usage_limit_per_user ?? 1}
                onChange={(e) => onChange({ ...draft, usage_limit_per_user: parseInt(e.target.value, 10) })}
              />
            </label>

            <div className="col-span-2 flex flex-wrap items-center gap-4 pt-1">
              <label className="flex items-center gap-2 text-sm">
                <Checkbox
                  checked={draft.is_active !== false}
                  onChange={(e) => onChange({ ...draft, is_active: e.target.checked })}
                />
                Active
              </label>
              <label className="flex items-center gap-2 text-sm">
                <Checkbox
                  checked={!!draft.first_time_users_only}
                  onChange={(e) => onChange({ ...draft, first_time_users_only: e.target.checked })}
                />
                First-time users only
              </label>
            </div>
            {error && <p className="col-span-2 text-sm text-destructive">{error}</p>}
          </div>

          <div className="mt-6 flex justify-end gap-2">
            <button
              type="button"
              onClick={onCancel}
              className="rounded-md border border-input px-3 py-1.5 text-sm font-medium hover:bg-accent"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={onSave}
              disabled={saving}
              className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
            >
              {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              Save
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
